import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="reset-password-card">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
            <Eye className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
          </div>
          <div className="font-[Outfit] font-semibold text-lg text-slate-900">QrewHR</div>
        </div>
        {!token ? (
          <div>
            <h2 className="font-semibold text-slate-900 text-xl">Invalid link</h2>
            <p className="text-sm text-slate-600 mt-2">This reset link is missing or malformed. Request a new one from the login page.</p>
            <Link to="/login" className="mt-6 inline-block text-cyan-700 hover:underline text-sm">← Back to login</Link>
          </div>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h2 className="mt-4 font-semibold text-slate-900 text-xl">Password updated</h2>
            <p className="text-sm text-slate-600 mt-2">You can now sign in with your new password.</p>
            <Button onClick={() => nav("/login")} className="mt-6 bg-slate-900 hover:bg-slate-800" data-testid="reset-goto-login">Go to login</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 className="font-semibold text-slate-900 text-xl">Set a new password</h2>
              <p className="text-sm text-slate-600 mt-1">Choose a strong password of at least 8 characters.</p>
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" autoFocus data-testid="reset-new-password" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5" data-testid="reset-confirm-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="reset-submit">{busy ? "Updating…" : "Update password"}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
